import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineConfirm from '../../src/components/InlineConfirm';
import InlineMessage from '../../src/components/InlineMessage';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';

interface TierItem {
  id: string; tierName: string; pricingModel: string; discountPercentage: number | null;
  monthlyFee: number | null; perWashRate: number | null; businessName: string; createdAt: string;
  description?: string; includedWashes?: number | null; minimumVehicles?: number | null;
}

const fmt = (cents: number | null) => cents != null ? `$${(cents / 100).toFixed(2)}` : '';

/** AdminFleetTiers - list, search, and manage fleet pricing tiers across all owners. */
export default function AdminFleetTiers() {
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const { ownerId: paramOwnerId } = useLocalSearchParams<{ ownerId?: string }>();
  const [tiers, setTiers] = useState<TierItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editItem, setEditItem] = useState<TierItem | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api(`/api/admin/fleet/tiers/${confirmDelete}`, { method: 'DELETE' });
      setConfirmDelete(null);
      setInlineMsg({ type: 'success', message: 'Fleet tier deleted' });
      fetchData(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setDeleting(false); }
  };

  const fetchData = useCallback(async (p = 1, s = '', ownerFilter = paramOwnerId, signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (ownerFilter) params.set('ownerId', ownerFilter);
      const data = await api<{ tiers: TierItem[]; total: number }>(`/api/admin/fleet/tiers?${params}`);
      if (signal?.aborted) return;
      setTiers(data.tiers);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  useEffect(() => { const ac = new AbortController(); fetchData(1, '', paramOwnerId, ac.signal); return () => ac.abort(); }, []);

  const openEdit = (item: TierItem) => {
    setEditItem(item);
    setEditData({
      tierName: item.tierName, pricingModel: item.pricingModel,
      discountPercentage: item.discountPercentage?.toString() || '',
      monthlyFee: item.monthlyFee?.toString() || '',
      perWashRate: item.perWashRate?.toString() || '',
      includedWashes: item.includedWashes?.toString() || '',
      minimumVehicles: item.minimumVehicles?.toString() || '',
    });
  };

  const handleSave = async () => {
    if (!editItem || !editData) return;
    setSaving(true);
    try {
      const body: any = { tierName: editData.tierName, pricingModel: editData.pricingModel };
      if (editData.discountPercentage) body.discountPercentage = parseFloat(editData.discountPercentage);
      if (editData.monthlyFee) body.monthlyFee = parseInt(editData.monthlyFee);
      if (editData.perWashRate) body.perWashRate = parseInt(editData.perWashRate);
      if (editData.includedWashes) body.includedWashes = parseInt(editData.includedWashes);
      if (editData.minimumVehicles) body.minimumVehicles = parseInt(editData.minimumVehicles);
      await api(`/api/admin/fleet/tiers/${editItem.id}`, { method: 'PUT', body });
      setEditItem(null);
      fetchData(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setSaving(false); }
  };

  const csvEndpoint = `/api/admin/fleet/tiers?${new URLSearchParams({ ...(search && { search }) })}`;

  const renderItem = ({ item }: { item: TierItem }) => (
    <View style={styles.row}>
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <Text style={styles.name}>{item.tierName}</Text>
          <View style={styles.modelBadge}>
            <Text style={styles.modelText}>{item.pricingModel.replace(/_/g, ' ')}</Text>
          </View>
        </View>
        <Text style={styles.meta}>
          {item.discountPercentage != null ? `${item.discountPercentage}% discount` : ''}
          {item.monthlyFee != null ? `Monthly: ${fmt(item.monthlyFee)}` : ''}
          {item.perWashRate != null ? `Per wash: ${fmt(item.perWashRate)}` : ''}
        </Text>
        <Text style={styles.sub}>Owner: {item.businessName}</Text>
      </View>
      <TouchableOpacity onPress={() => openEdit(item)} hitSlop={8}>
        <Ionicons name="create-outline" size={18} color={colors.primary} />
      </TouchableOpacity>
      <TouchableOpacity onPress={() => setConfirmDelete(item.id)} hitSlop={8}>
        <Ionicons name="trash-outline" size={18} color={colors.danger} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search)}
        total={total} page={page} limit={20} onPageChange={(p) => fetchData(p, search)}
        csvEndpoint={csvEndpoint} csvFilename="fleet-tiers.csv" label="tier"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={tiers} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No fleet tiers found</Text>}
        />
      )}

      <InlineConfirm
        message={`Delete fleet tier "${tiers.find(t => t.id === confirmDelete)?.tierName ?? ''}"?`}
        visible={!!confirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />
      <InlineMessage type={inlineMsg?.type || 'error'} message={inlineMsg?.message || ''} visible={!!inlineMsg && !editItem} onDismiss={() => setInlineMsg(null)} />

      <Modal visible={!!editItem} transparent animationType="slide" onRequestClose={() => setEditItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Fleet Tier</Text>
            {editData && (
              <ScrollView nestedScrollEnabled>
                <Text style={styles.fieldLabel}>Tier Name</Text>
                <TextInput style={styles.fieldInput} value={editData.tierName} onChangeText={(v) => setEditData({ ...editData, tierName: v })} maxLength={32} />
                <Text style={styles.fieldLabel}>Pricing Model</Text>
                <View style={styles.chipRow}>
                  {['percentage_discount', 'fixed_monthly', 'per_wash_rate'].map(m => (
                    <TouchableOpacity key={m} onPress={() => setEditData({ ...editData, pricingModel: m })}
                      style={[styles.chip, editData.pricingModel === m && styles.chipActive]}>
                      <Text style={[styles.chipText, editData.pricingModel === m && { color: colors.white }]}>
                        {m.replace(/_/g, ' ')}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <Text style={styles.fieldLabel}>Discount Percentage</Text>
                <TextInput style={styles.fieldInput} value={editData.discountPercentage} onChangeText={(v) => setEditData({ ...editData, discountPercentage: v })} keyboardType="decimal-pad" placeholder="e.g. 15" placeholderTextColor={colors.textLight} maxLength={5} />
                <Text style={styles.fieldLabel}>Monthly Fee (cents)</Text>
                <TextInput style={styles.fieldInput} value={editData.monthlyFee} onChangeText={(v) => setEditData({ ...editData, monthlyFee: v })} keyboardType="number-pad" maxLength={10} />
                <Text style={styles.fieldLabel}>Per Wash Rate (cents)</Text>
                <TextInput style={styles.fieldInput} value={editData.perWashRate} onChangeText={(v) => setEditData({ ...editData, perWashRate: v })} keyboardType="number-pad" maxLength={10} />
                <Text style={styles.fieldLabel}>Included Washes</Text>
                <TextInput style={styles.fieldInput} value={editData.includedWashes} onChangeText={(v) => setEditData({ ...editData, includedWashes: v })} keyboardType="number-pad" maxLength={6} />
                <Text style={styles.fieldLabel}>Minimum Vehicles</Text>
                <TextInput style={styles.fieldInput} value={editData.minimumVehicles} onChangeText={(v) => setEditData({ ...editData, minimumVehicles: v })} keyboardType="number-pad" maxLength={6} />
              </ScrollView>
            )}
            <InlineMessage type={inlineMsg?.type || 'error'} message={inlineMsg?.message || ''} visible={!!inlineMsg} onDismiss={() => setInlineMsg(null)} />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditItem(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
                <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 8 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  modelBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, backgroundColor: c.primaryBg },
  modelText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.primary, textTransform: 'capitalize' },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  sub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  fieldInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, marginTop: 4, borderWidth: 1, borderColor: c.border },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  chipActive: { backgroundColor: c.primary, borderColor: c.primary },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium, textTransform: 'capitalize' },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
});
const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
